import React, { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

/**
 * Portfolio Mixed-Media V1 — mixed-media gallery tile.
 *
 * One component with two branches:
 *   • kind === "image" → plain <img loading="lazy">, browser-native aspect.
 *   • kind === "video" → <video muted playsInline loop preload="metadata">
 *     wrapped in an aspect-locked container so the layout box is reserved
 *     BEFORE metadata loads (zero cumulative layout shift).
 *
 * Playback rules (from spec):
 *   • Autoplay only when meaningfully visible (IntersectionObserver ≥ 55%)
 *   • Pause when off-screen
 *   • Respect `prefers-reduced-motion: reduce` — never autoplay, show
 *     poster, require a deliberate tap.
 *   • Muted + playsInline (Safari autoplay compliance).
 *   • Tap toggles play/pause. No native browser control bar.
 *   • Only ONE video plays at a time — a global custom event
 *     ("ur:media:play") pauses every other mounted video. Cheap, no
 *     external state library.
 */
const ORIENTATION_ASPECT = {
  landscape: "aspect-video", // 16:9
  portrait: "aspect-[9/16]", // 9:16
  square: "aspect-square", // 1:1
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const PortfolioMediaTile = ({ asset, alt, testid }) => {
  const kind = asset?.kind === "video" ? "video" : "image";
  const orientation = asset?.orientation || "landscape";
  const src = asset?.url || "";
  const poster = asset?.poster || undefined;
  const caption = asset?.alt || alt || "";

  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [reduceMotion] = useState(prefersReducedMotion);

  useEffect(() => {
    if (kind !== "video") return undefined;
    const video = videoRef.current;
    if (!video) return undefined;

    // Global "only one video at a time" bus.
    const onOther = (e) => {
      if (e.detail?.id !== video) {
        try { video.pause(); } catch {}
        setPlaying(false);
      }
    };
    window.addEventListener("ur:media:play", onOther);

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.55) {
            try { video.pause(); } catch {}
            setPlaying(false);
            return;
          }
          if (reduceMotion) return;
          window.dispatchEvent(new CustomEvent("ur:media:play", { detail: { id: video } }));
          const p = video.play();
          if (p && typeof p.then === "function") p.then(() => setPlaying(true)).catch(() => setPlaying(false));
        });
      },
      { threshold: [0, 0.55, 1] }
    );
    if (containerRef.current) io.observe(containerRef.current);
    return () => {
      io.disconnect();
      window.removeEventListener("ur:media:play", onOther);
    };
  }, [kind, reduceMotion]);

  const toggle = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      window.dispatchEvent(new CustomEvent("ur:media:play", { detail: { id: video } }));
      const p = video.play();
      if (p && typeof p.then === "function") p.then(() => setPlaying(true)).catch(() => {});
    } else {
      video.pause();
      setPlaying(false);
    }
  };

  if (kind === "image") {
    return (
      <div
        data-testid={testid}
        data-kind="image"
        className="rounded-2xl overflow-hidden border border-[#262626] bg-[#141414]"
      >
        <img
          src={src}
          alt={caption}
          loading="lazy"
          className="w-full h-auto object-cover block"
        />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      data-testid={testid}
      data-kind="video"
      data-orientation={orientation}
      className={`relative rounded-2xl overflow-hidden border border-[#262626] bg-[#0B0B0B] ${ORIENTATION_ASPECT[orientation] || ORIENTATION_ASPECT.landscape}`}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        muted
        playsInline
        loop
        preload="metadata"
        className="absolute inset-0 w-full h-full object-cover"
        aria-label={caption || undefined}
      />
      <button
        type="button"
        data-testid={testid ? `${testid}-toggle` : undefined}
        onClick={toggle}
        aria-label={playing ? "Pause video" : "Play video"}
        className="absolute inset-0 grid place-items-center bg-black/0 hover:bg-black/10 transition-colors"
      >
        {!playing && (
          <span className="w-14 h-14 rounded-full bg-black/50 backdrop-blur-sm grid place-items-center border border-white/20">
            <Play className="w-6 h-6 text-white ml-1" strokeWidth={2} />
          </span>
        )}
        {playing && (
          <span className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm grid place-items-center opacity-0 hover:opacity-100 transition-opacity">
            <Pause className="w-4 h-4 text-white" strokeWidth={2} />
          </span>
        )}
      </button>
    </div>
  );
};

/**
 * Resolve a project's rendered gallery — reads new structured `gallery`
 * first and falls back to legacy `gallery_images` mapped as image assets
 * so pre-Mixed-Media projects render unchanged.
 */
export const resolveGallery = (project) => {
  if (Array.isArray(project?.gallery) && project.gallery.length > 0) {
    return project.gallery
      .filter((a) => a && a.url)
      .map((a) => ({
        kind: a.kind === "video" ? "video" : "image",
        url: a.url,
        poster: a.poster || null,
        orientation: a.orientation || "landscape",
        alt: a.alt || "",
      }));
  }
  const legacy = Array.isArray(project?.gallery_images) ? project.gallery_images : [];
  return legacy.filter(Boolean).map((url) => ({
    kind: "image",
    url,
    poster: null,
    orientation: "landscape",
    alt: "",
  }));
};

/**
 * Given a mixed-media asset, return the tailwind `col-span` that keeps
 * the detail-page grid editorial rather than uniform:
 *   • landscape → col-span-2 (full-width row)
 *   • portrait  → col-span-1 (pairs neatly with other portraits)
 *   • square    → col-span-1
 * Images without an explicit orientation default to full-width so legacy
 * projects keep their current single-column feel.
 */
export const spanForOrientation = (orientation) => {
  if (orientation === "portrait" || orientation === "square") return "sm:col-span-1";
  return "sm:col-span-2";
};
